import React, { useEffect, useState, useCallback } from 'react';
import { Badge } from '@/components/ui/badge';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import { Loader2 } from 'lucide-react';
import {
  getVoiceProvider,
  setVoiceProvider,
  type VoiceProvider,
} from '@/services/voiceProviderService';

const PROVIDER_LABELS: Record<VoiceProvider, string> = {
  telnyx: 'Telnyx',
  twilio: 'Twilio',
};

const PROVIDER_COLORS: Record<VoiceProvider, string> = {
  telnyx: 'bg-blue-100 text-blue-700 hover:bg-blue-200 border-blue-200 dark:bg-blue-900/30 dark:text-blue-300 dark:border-blue-800',
  twilio: 'bg-purple-100 text-purple-700 hover:bg-purple-200 border-purple-200 dark:bg-purple-900/30 dark:text-purple-300 dark:border-purple-800',
};

export const VoiceProviderToggle: React.FC = () => {
  const [provider, setProvider] = useState<VoiceProvider>('telnyx');
  const [ready, setReady] = useState(false);
  const [switching, setSwitching] = useState(false);

  const fetchProvider = useCallback(async () => {
    try {
      const res = await getVoiceProvider();
      setProvider(res.provider);
    } catch {
      // keep default 'telnyx' — still render the badge
    } finally {
      setReady(true);
    }
  }, []);

  useEffect(() => {
    fetchProvider();
  }, [fetchProvider]);

  const handleToggle = async () => {
    if (switching) return;
    const next: VoiceProvider = provider === 'telnyx' ? 'twilio' : 'telnyx';
    setSwitching(true);
    // Optimistic update
    setProvider(next);
    try {
      const res = await setVoiceProvider(next);
      setProvider(res.provider);
    } catch {
      // revert
      setProvider(provider);
    } finally {
      setSwitching(false);
    }
  };

  if (!ready) {
    return <Loader2 className="h-3 w-3 animate-spin text-muted-foreground" />;
  }

  return (
    <TooltipProvider delayDuration={300}>
      <Tooltip>
        <TooltipTrigger asChild>
          <button
            onClick={handleToggle}
            disabled={switching}
            className="focus:outline-none"
            aria-label={`Voice provider: ${PROVIDER_LABELS[provider]}. Click to switch.`}
          >
            <Badge
              variant="outline"
              className={`text-[10px] px-1.5 py-0 cursor-pointer transition-all select-none ${PROVIDER_COLORS[provider]} ${switching ? 'opacity-50' : ''}`}
            >
              {switching ? (
                <Loader2 className="h-2.5 w-2.5 animate-spin" />
              ) : (
                PROVIDER_LABELS[provider]
              )}
            </Badge>
          </button>
        </TooltipTrigger>
        <TooltipContent side="bottom" className="text-xs">
          <p>Using <strong>{PROVIDER_LABELS[provider]}</strong> for calls</p>
          <p className="text-muted-foreground mt-0.5">
            Click to switch to {PROVIDER_LABELS[provider === 'telnyx' ? 'twilio' : 'telnyx']}
          </p>
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
};
